/*
 * Purpose: Client API — Mission Director enrichment (parallel RAG + discovery)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { apiUrl } from '@/lib/apiBase';
import type { MissionDirectorPhase } from '@/lib/missionDirectorPolicy';
import type { IntegrationDiscoveryProposal } from '@/services/integrations/types';

export interface MissionDirectorEnrichmentResult {
  ok: boolean;
  phase?: MissionDirectorPhase;
  missionSessionId?: string;
  elapsedMs?: number;
  specialists?: {
    rag?: {
      ok?: boolean;
      contextBlock?: string;
      chunkCount?: number;
      message?: string;
    };
    discovery?: {
      ok?: boolean;
      proposals?: IntegrationDiscoveryProposal[];
      searchProvider?: string | null;
      message?: string;
      error?: string;
      action?: { label: string; href?: string };
    };
  };
  error?: string;
}

export async function fetchMissionDirectorEnrichment(input: {
  missionSessionId: string;
  outcome: string;
  interpretedGoal?: string;
  gaps?: string[];
  existingCatalog?: string;
  phase?: MissionDirectorPhase;
}): Promise<MissionDirectorEnrichmentResult> {
  const { missionSessionId, outcome } = input;
  if (!missionSessionId || !outcome.trim()) {
    return { ok: false, error: 'missionSessionId and outcome required' };
  }

  try {
    const res = await fetch(
      apiUrl(`/api/missions/${encodeURIComponent(missionSessionId)}/director/enrich`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: outcome.trim(),
          interpretedGoal: input.interpretedGoal?.trim(),
          gaps: input.gaps ?? [],
          existingCatalog: input.existingCatalog ?? '',
          phase: input.phase ?? 'enrich',
        }),
      }
    );

    const body = (await res.json()) as MissionDirectorEnrichmentResult & { error?: string };

    if (res.status === 401) {
      return { ok: false, error: 'Sign in required' };
    }

    if (!res.ok) {
      return { ok: false, error: body.error ?? `Director enrich failed (${res.status})` };
    }

    return body;
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Director enrich unavailable',
    };
  }
}
